/**
 * Bundle 80.2 Task 2 - Admin testimonials showcase.
 * URL: /admin/testimonials/showcase
 *
 * Lists every published (+ revoked-for-audit) testimonial across all
 * events. Admins can:
 *   - Toggle Featured on / off (only when status=published)
 *   - Drag to reorder featured entries (uses HTML5 native drag events)
 *   - Unpublish (moves back to `approved`, hides from public API)
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, GripVertical, Share2 } from 'lucide-react';

const BULK_ACTIONS = [
  { value: 'approve', label: 'Approve' },
  { value: 'publish', label: 'Publish' },
  { value: 'unpublish', label: 'Unpublish' },
  { value: 'feature', label: 'Feature (max 12)' },
  { value: 'unfeature', label: 'Unfeature' },
  { value: 'reject', label: 'Reject (needs reason)' },
];

const AdminTestimonialsShowcasePage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('approve');
  const [bulkReason, setBulkReason] = useState('');
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/testimonials/showcase');
      setRows(data?.testimonials || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load showcase');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleFeatured = async (t) => {
    try {
      await api.put(`/admin/testimonials/${t.id}/featured`, {
        is_featured: !t.is_featured,
      });
      toast.success(t.is_featured ? 'Removed from featured.' : 'Added to featured.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    }
  };

  const unpublish = async (t) => {
    if (!window.confirm('Unpublish this testimonial? It will disappear from the public site.')) return;
    try {
      await api.post(`/admin/testimonials/${t.id}/unpublish`, {});
      toast.success('Unpublished.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unpublish failed');
    }
  };

  const openShareCard = (t) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/testimonials/${t.id}/share-card.html`;
    window.open(url, '_blank');
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelected(new Set(rows.filter((r) => !r.revoked_at).map((r) => r.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one row.');
      return;
    }
    if (bulkAction === 'reject' && bulkReason.trim().length < 8) {
      toast.error('Reject needs a reason (min 8 characters).');
      return;
    }
    if ((bulkAction === 'unpublish' || bulkAction === 'reject')
      && !window.confirm(`Apply ${bulkAction} to ${selected.size} row(s)?`)) {
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post('/admin/testimonials/bulk-action', {
        action: bulkAction,
        ids: Array.from(selected),
        reason: bulkReason.trim(),
      });
      toast.success(`${bulkAction}: ${data.changed}/${data.requested} rows updated.`);
      clearSelection();
      setBulkReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed');
    } finally {
      setRunning(false);
    }
  };

  const featured = useMemo(
    () => rows.filter((r) => r.is_featured && r.status === 'published')
                 .sort((a, b) => (a.featured_order || 999) - (b.featured_order || 999)),
    [rows],
  );
  const others = useMemo(
    () => rows.filter((r) => !r.is_featured || r.status !== 'published'),
    [rows],
  );

  const onDragStart = (t) => setDragging(t.id);
  const onDragOver = (e) => e.preventDefault();
  const onDropOn = async (target) => {
    if (!dragging || dragging === target.id) return;
    const currentIds = featured.map((r) => r.id);
    const from = currentIds.indexOf(dragging);
    const to = currentIds.indexOf(target.id);
    if (from < 0 || to < 0) return;
    const reordered = [...currentIds];
    reordered.splice(from, 1);
    reordered.splice(to, 0, dragging);
    setDragging(null);
    try {
      await api.put('/admin/testimonials/reorder', { ordered_ids: reordered });
      toast.success('Order saved.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reorder failed');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="admin-testimonials-showcase-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800">Bundle 80.2 * Testimonials</div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mt-1">Showcase manager</h1>
          <p className="text-neutral-600 text-sm mt-1">
            Curate the six-quote home carousel. Drag featured entries to reorder.
            Revoked entries stay here for audit and never surface publicly.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}
          data-testid="showcase-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error ? <div className="text-rose-700 text-xs mb-3">{error}</div> : null}

      {/* Bundle 83 Task 2a - bulk moderation bar */}
      <div className="border border-neutral-200 rounded-md p-3 mb-6 bg-neutral-50 flex flex-wrap items-center gap-2"
            data-testid="bulk-moderation-bar">
        <span className="text-xs text-primary-900 font-medium">
          {selected.size} selected
        </span>
        <Button size="sm" variant="outline" onClick={selectAllVisible}
                data-testid="bulk-select-all-btn">Select all</Button>
        <Button size="sm" variant="ghost" onClick={clearSelection}
                data-testid="bulk-clear-btn">Clear</Button>
        <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}
                className="border border-neutral-200 rounded-md px-2 py-1 text-sm"
                data-testid="bulk-action-selector">
          {BULK_ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
        {bulkAction === 'reject' ? (
          <input
            type="text"
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
            placeholder="Reason (min 8 characters)"
            className="border border-neutral-200 rounded-md px-2 py-1 text-sm flex-1 min-w-[200px]"
            data-testid="bulk-reason-input"
          />
        ) : null}
        <Button size="sm" onClick={runBulk} disabled={running || selected.size === 0}
                className="bg-primary-900 text-white hover:bg-primary-800"
                data-testid="bulk-apply-btn">
          {running ? 'Applying...' : 'Apply'}
        </Button>
      </div>

      <div className="mb-8">
        <h2 className="font-display text-lg text-primary-900 mb-2">
          Featured on home ({featured.length})
        </h2>
        {featured.length === 0 ? (
          <div className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-md p-4">
            Nothing featured yet. Tick &ldquo;Featured on home&rdquo; below to add.
          </div>
        ) : (
          <div className="space-y-2" data-testid="showcase-featured-list">
            {featured.map((t) => (
              <ShowcaseRow
                key={t.id} t={t} draggable
                dragging={dragging}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDropOn={onDropOn}
                onToggleFeatured={toggleFeatured}
                onUnpublish={unpublish}
                onOpenShare={openShareCard}
                selected={selected.has(t.id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display text-lg text-primary-900 mb-2">
          Everything else ({others.length})
        </h2>
        <div className="space-y-2" data-testid="showcase-others-list">
          {others.map((t) => (
            <ShowcaseRow
              key={t.id} t={t}
              onToggleFeatured={toggleFeatured}
              onUnpublish={unpublish}
              onOpenShare={openShareCard}
              selected={selected.has(t.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
          {others.length === 0 && !loading ? (
            <div className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-md p-4">
              No other testimonials on file yet.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ShowcaseRow = ({
  t, draggable = false, onDragStart, onDragOver, onDropOn,
  onToggleFeatured, onUnpublish, onOpenShare,
  selected = false, onToggleSelect,
}) => (
  <div
    className={`border border-neutral-200 rounded-md p-3 flex items-start gap-3 bg-white ${
      draggable ? 'cursor-move' : ''
    } ${selected ? 'ring-2 ring-primary-400' : ''}`}
    draggable={draggable}
    onDragStart={() => draggable && onDragStart && onDragStart(t)}
    onDragOver={onDragOver}
    onDrop={() => draggable && onDropOn && onDropOn(t)}
    data-testid={`showcase-row-${t.id}`}
  >
    {onToggleSelect ? (
      <input
        type="checkbox"
        className="mt-1"
        checked={!!selected}
        onChange={() => onToggleSelect(t.id)}
        disabled={!!t.revoked_at}
        data-testid={`showcase-select-${t.id}`}
      />
    ) : null}
    {draggable ? (
      <GripVertical className="h-4 w-4 text-neutral-400 mt-1 shrink-0" />
    ) : null}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Badge variant="outline" className="capitalize">
          {(t.status || 'published').replace(/_/g, ' ')}
        </Badge>
        {t.revoked_at ? (
          <Badge className="bg-rose-100 text-rose-800 border-rose-200">Revoked</Badge>
        ) : null}
        {t.is_featured ? (
          <Badge className="bg-secondary-100 text-secondary-800 border-secondary-200">Featured</Badge>
        ) : null}
        {t.event_title ? <span className="text-neutral-600">{t.event_title}</span> : null}
      </div>
      <div className="mt-2 text-primary-900 italic text-sm leading-relaxed"
            data-testid={`showcase-quote-${t.id}`}>
        {t.quote_text_final || (
          <span className="text-neutral-400 not-italic">
            Testimonial removed at guest request.
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-neutral-500 flex flex-wrap items-center gap-2">
        <span>{t.attribution_display || 'Anonymous'} * {t.attribution}</span>
        {t.needs_last_name ? (
          <span
            className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-800"
            data-testid={`needs-last-name-${t.id}`}
            title="Backfill could not resolve a last name. Add one on the registration record."
          >
            Needs last name
            {t.registration_id ? (
              <>
                {' '}
                <Link
                  to={`/admin/registrations/${t.registration_id}`}
                  className="ml-1 underline"
                  data-testid={`needs-last-name-link-${t.id}`}
                >Edit</Link>
              </>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
    <div className="flex flex-col gap-2 shrink-0">
      {t.status === 'published' && !t.revoked_at ? (
        <>
          <label className="text-xs flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!t.is_featured}
              onChange={() => onToggleFeatured(t)}
              data-testid={`showcase-featured-toggle-${t.id}`}
            />
            Featured on home
          </label>
          <Button size="sm" variant="outline" onClick={() => onOpenShare(t)}
            data-testid={`showcase-share-btn-${t.id}`}>
            <Share2 className="h-3.5 w-3.5 mr-1" /> Preview share card
          </Button>
          <Button size="sm" variant="outline" onClick={() => onUnpublish(t)}
            data-testid={`showcase-unpublish-btn-${t.id}`}>
            Unpublish
          </Button>
        </>
      ) : null}
    </div>
  </div>
);

export default AdminTestimonialsShowcasePage;
